import { arrayMove } from "@dnd-kit/sortable";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { SlidingCubeLoader } from "react-loaders-kit";
import useAuthStore from "../../../../store/useAuthStore";
import useCompanyStore from "../../../../store/useCompanyStore";
import DateRange from "./DateRangeSelector";
import Modal from "./Modal";
import { format } from "date-fns";
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { Download, FileText, Table } from 'lucide-react';

const safeString = (str) =>
  typeof str === "string" ? str.toLowerCase().trim() : "";

const TrialBalance = () => {
  const [accounts, setAccounts] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0 });
  const [columns, setColumns] = useState([
    { id: "code", label: "Account Code" },
    { id: "name", label: "Account Name" },
    { id: "type", label: "Account Type" },
    { id: "debit", label: "Debit" },
    { id: "credit", label: "Credit" },
  ]);
  const [editing, setEditing] = useState(false);
  const [trialBalanceTitle, setTrialBalanceTitle] = useState("Trial Balance");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [dateRangeText, setDateRangeText] = useState("");

  const { token } = useAuthStore();
  const { companyInfo } = useCompanyStore();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const headers = {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        };
        const response = await axios.get("/api/getAllAccounts", { headers });
        const accountsData = response.data?.accounts || [];
        setAccounts(accountsData);
        setFilteredAccounts(accountsData);
      } catch (error) {
        console.error("Error fetching accounts:", error.message);
      }
    };
    fetchAccounts();
  }, [token]);

  const updateFilteredData = async ({ startDate, endDate }) => {
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;
    if (!start || !end) return;
    setLoading(true);
    setDateRangeText(
      `From ${start.toLocaleDateString()} to ${end.toLocaleDateString()}`
    );
    try {
      const headers = {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      };

      const startStr = start.toISOString().split("T")[0];
      const endStr = end.toISOString().split("T")[0];

      const [expenseResponse, incomeResponse] = await Promise.all([
        axios.get(`/api/getallexpense?startDate=${startStr}&endDate=${endStr}&limit=-1`, { headers }),
        axios.get(`/api/getallIncomes?startDate=${startStr}&endDate=${endStr}&limit=-1`, { headers }),
      ]);

      const filteredExpenses = expenseResponse.data?.expenses || [];
      const filteredIncomes = incomeResponse.data?.incomes || [];



      const updatedAccounts = accounts.map((account) => {
        const totalDebit = filteredExpenses
          .filter(
            (expense) =>
              safeString(expense.subheader) ===
              safeString(`${account.code}-${account.name}`) &&
              safeString(expense.header) === safeString(account.type)
          )
          .reduce((sum, txn) => sum + txn.amount, 0);
        const totalCredit = filteredIncomes
          .filter(
            (income) =>
              safeString(income.subheader) ===
              safeString(`${account.code}-${account.name}`) &&
              safeString(income.header) === safeString(account.type)
          )
          .reduce((sum, txn) => sum + txn.amount, 0);
        const ytd =
          totalDebit === totalCredit
            ? "0.0"
            : totalDebit > totalCredit
              ? `${(totalDebit - totalCredit).toFixed(2)} Dr`
              : `${(totalCredit - totalDebit).toFixed(2)} Cr`;
        return { ...account, ytd: ytd || "0.0" };
      });
      setFilteredAccounts(updatedAccounts);
      let totalDebit = 0;
      let totalCredit = 0;
      updatedAccounts.forEach((account) => {
        const ytdValue = account.ytd || "0.0";
        const value = parseFloat(ytdValue);
        if (ytdValue.toLowerCase().endsWith("dr")) {
          totalDebit += value;
        } else if (ytdValue.toLowerCase().endsWith("cr")) {
          totalCredit += value;
        }
      });
      setTotals({ totalDebit, totalCredit });
    } catch (error) {
      console.error("Error updating filtered data:", error.message);
    } finally {
      setLoading(false);
    }
  };

  // Export to PDF function
  const exportToPDF = async () => {
    const doc = new jsPDF('p', 'mm', 'a4');
    const pageWidth = doc.internal.pageSize.width;
    const margin = 20;

    // Add company logo if available
    if (companyInfo?.logo) {
      try {
        const logoSize = 30; // Increased logo size
        const logoX = pageWidth - margin - logoSize;
        const logoY = 10;
        doc.addImage(companyInfo.logo, 'PNG', logoX, logoY, logoSize, logoSize);
      } catch (error) {
        console.error('Error adding logo to PDF:', error);
      }
    }

    // Company Header
    doc.setFontSize(18);
    doc.setFont(undefined, 'bold');
    const companyName = companyInfo?.name || "NO COMPANY NAME";
    doc.text(companyName, pageWidth / 2, 25, { align: 'center' });

    // Report Title
    doc.setFontSize(14);
    doc.text(trialBalanceTitle, pageWidth / 2, 35, { align: 'center' });

    // Date Range
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const dateText = dateRangeText || `As of: ${format(new Date(), "MMMM dd, yyyy")}`;
    doc.text(dateText, pageWidth / 2, 42, { align: 'center' });

    // Prepare table data
    const tableData = [];
    const filteredAccountsData = filteredAccounts.filter((account) => {
      const ytdValue = account.ytd || "";
      const debit = parseFloat(
        ytdValue.toLowerCase().endsWith("dr") ? ytdValue : "0.0"
      );
      const credit = parseFloat(
        ytdValue.toLowerCase().endsWith("cr") ? ytdValue : "0.0"
      );
      return debit !== 0 || credit !== 0;
    });

    // Account rows
    filteredAccountsData.forEach(account => {
      const row = columns.map(col => {
        if (col.id === "code") return account.code || "N/A";
        if (col.id === "name") return account.name;
        if (col.id === "type") return account.type;
        if (col.id === "debit") {
          return parseFloat(
            account.ytd.toLowerCase().endsWith("dr") ? account.ytd : "0.0"
          ).toFixed(2);
        }
        if (col.id === "credit") {
          return parseFloat(
            account.ytd.toLowerCase().endsWith("cr") ? account.ytd : "0.0"
          ).toFixed(2);
        }
        return "";
      });
      tableData.push(row);
    });

    // Total row
    if (totals.totalDebit > 0 || totals.totalCredit > 0) {
      const totalRow = columns.map(col => {
        if (col.id === "code" || col.id === "name") return col.id === "code" ? "" : "TOTAL";
        if (col.id === "type") return "";
        if (col.id === "debit") return totals.totalDebit.toFixed(2);
        if (col.id === "credit") return totals.totalCredit.toFixed(2);
        return "";
      });

      tableData.push(totalRow.map(cell => ({
        content: cell,
        styles: { fontStyle: 'bold', fillColor: [191, 219, 254], textColor: [30, 64, 175] }
      })));
    }

    // Create table
    const tableHeaders = columns.map(col => col.label);

    doc.autoTable({
      head: [tableHeaders],
      body: tableData,
      startY: 50,
      margin: { left: margin, right: margin },
      styles: {
        fontSize: 9,
        cellPadding: 3,
      },
      headStyles: {
        fillColor: [59, 130, 246],
        textColor: 255,
        fontStyle: 'bold',
        halign: 'center'
      },
      columnStyles: {
        0: { halign: 'left' },
        1: { halign: 'left' },
        2: { halign: 'left' },
        3: { halign: 'right' },
        4: { halign: 'right' },
      },
      didDrawPage: function (data) {
        // Footer
        const pageNumber = doc.internal.getNumberOfPages();
        doc.setFontSize(8);
        doc.text(`Page ${data.pageNumber} of ${pageNumber}`, pageWidth - margin, doc.internal.pageSize.height - 10, { align: 'right' });
      }
    });

    doc.save(`${trialBalanceTitle.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    setShowExportOptions(false);
  };

  // Export to CSV function
  const exportToCSV = () => {
    let csvContent = '';

    // Header
    csvContent += `${companyInfo?.name || "NO COMPANY NAME"}\n`;
    csvContent += `${trialBalanceTitle}\n`;
    const dateText = dateRangeText || `As of: ${format(new Date(), "MMMM dd, yyyy")}`;
    csvContent += `${dateText}\n\n`;

    // Table headers
    const headers = columns.map(col => col.label);
    csvContent += headers.join(',') + '\n';

    // Account data
    const filteredAccountsData = filteredAccounts.filter((account) => {
      const ytdValue = account.ytd || "";
      const debit = parseFloat(
        ytdValue.toLowerCase().endsWith("dr") ? ytdValue : "0.0"
      );
      const credit = parseFloat(
        ytdValue.toLowerCase().endsWith("cr") ? ytdValue : "0.0"
      );
      return debit !== 0 || credit !== 0;
    });

    filteredAccountsData.forEach(account => {
      const row = columns.map(col => {
        if (col.id === "code") return account.code || "N/A";
        if (col.id === "name") return account.name;
        if (col.id === "type") return account.type;
        if (col.id === "debit") {
          return parseFloat(
            account.ytd.toLowerCase().endsWith("dr") ? account.ytd : "0.0"
          ).toFixed(2);
        }
        if (col.id === "credit") {
          return parseFloat(
            account.ytd.toLowerCase().endsWith("cr") ? account.ytd : "0.0"
          ).toFixed(2);
        }
        return "";
      });
      csvContent += row.join(',') + '\n';
    });

    // Total row
    if (totals.totalDebit > 0 || totals.totalCredit > 0) {
      csvContent += '\n';
      const totalRow = columns.map(col => {
        if (col.id === "code") return "";
        if (col.id === "name") return "TOTAL";
        if (col.id === "type") return "";
        if (col.id === "debit") return totals.totalDebit.toFixed(2);
        if (col.id === "credit") return totals.totalCredit.toFixed(2);
        return "";
      });
      csvContent += totalRow.join(',') + '\n';
    }

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `${trialBalanceTitle.replace(/\s+/g, '_')}_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setShowExportOptions(false);
  };

  const isTotalHeadingVisible =
    ![columns[0]?.id, columns[1]?.id].includes("debit") &&
    ![columns[0]?.id, columns[1]?.id].includes("credit");

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm">
      <div className="mb-8 flex flex-col items-center justify-center">
        {companyInfo?.logo && (
          <img
            src={companyInfo.logo}
            alt="Company Logo"
            className="h-24 w-auto object-contain mb-4"
          />
        )}

        <h1 className="text-3xl font-extrabold text-gray-900 mb-2 text-center">
          {companyInfo ? companyInfo.name : "NO COMPANY NAME"}
        </h1>

        <div className="flex justify-center mb-2">
          <h2
            className="text-xl font-bold text-gray-700 cursor-pointer hover:text-blue-600 transition-colors"
            onClick={() => setEditing(true)}
          >
            {editing ? (
              <input
                type="text"
                value={trialBalanceTitle}
                onChange={(e) => setTrialBalanceTitle(e.target.value)}
                onBlur={() => setEditing(false)}
                autoFocus
                className="border-b-2 border-gray-300 focus:outline-none focus:border-blue-500 text-center bg-transparent"
              />
            ) : (
              trialBalanceTitle
            )}
          </h2>
        </div>

        <p className="text-center font-medium text-gray-500 text-sm">
          {dateRangeText || "Select a date range to view."}
        </p>

        {/* Export Button */}
        {dateRangeText && (
          <div className="relative mt-4 flex justify-center">
            <button
              onClick={() => setShowExportOptions(!showExportOptions)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg shadow-sm transition-colors duration-200 font-medium"
            >
              <Download size={18} />
              Export Report
            </button>

            {/* Export Options Dropdown */}
            {showExportOptions && (
              <div className="absolute top-full mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-10 min-w-[180px]">
                <button
                  onClick={exportToPDF}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
                >
                  <FileText size={16} className="text-blue-500" />
                  <span>Export as PDF</span>
                </button>
                <button
                  onClick={exportToCSV}
                  className="w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 text-gray-700 transition-colors duration-200"
                >
                  <Table size={16} className="text-blue-600" />
                  <span>Export as CSV</span>
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      <DateRange
        onFilteredDataChange={updateFilteredData}
        setLoading={setLoading}
        loading={loading}
      />

      {loading ? (
        <div className="flex justify-center items-center min-h-screen">
          <SlidingCubeLoader
            loading={true}
            size={50}
            colors={["#2887D4BE", "#0976C3BE"]}
            duration={1}
          />
        </div>
      ) : (
        <>
          <button
            onClick={() => setIsModalVisible(true)}
            className="bg-blue-500 text-white px-4 py-2 rounded mt-4"
          >
            Reorder Columns
          </button>
          <Modal
            columns={columns}
            isModalVisible={isModalVisible}
            onDragEnd={(event) => {
              const { active, over } = event;
              if (active.id !== over.id) {
                setColumns((prevColumns) => {
                  const oldIndex = prevColumns.findIndex(
                    (col) => col.id === active.id
                  );
                  const newIndex = prevColumns.findIndex(
                    (col) => col.id === over.id
                  );
                  return arrayMove(prevColumns, oldIndex, newIndex);
                });
              }
            }}
            onApplyChanges={() => setIsModalVisible(false)}
            onCancel={() => setIsModalVisible(false)}
          />
          <table className="table-auto border-collapse w-full mt-4">
            <thead>
              <tr className="border-b border-gray-400">
                {columns.map((col) => (
                  <th key={col.id} className="px-4 py-2 text-left">
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredAccounts.length > 0 ? (
                filteredAccounts
                  .filter((account) => {
                    const ytdValue = account.ytd || "";
                    const debit = parseFloat(
                      ytdValue.toLowerCase().endsWith("dr")
                        ? ytdValue
                        : "0.0"
                    );
                    const credit = parseFloat(
                      ytdValue.toLowerCase().endsWith("cr")
                        ? ytdValue
                        : "0.0"
                    );
                    return debit !== 0 || credit !== 0;
                  })
                  .map((account, index) => (
                    <tr key={index} className="border-b border-gray-300">
                      {columns.map((col) => (
                        <td
                          key={col.id}
                          className={`px-4 py-2 ${col.id === "debit" || col.id === "credit"
                            ? "text-right"
                            : ""
                            }`}
                        >
                          {col.id === "code"
                            ? account.code || "N/A"
                            : col.id === "name"
                              ? account.name
                              : col.id === "type"
                                ? account.type
                                : col.id === "debit"
                                  ? parseFloat(
                                    account.ytd.toLowerCase().endsWith("dr")
                                      ? account.ytd
                                      : "0.0"
                                  ).toFixed(2)
                                  : col.id === "credit"
                                    ? parseFloat(
                                      account.ytd.toLowerCase().endsWith("cr")
                                        ? account.ytd
                                        : "0.0"
                                    ).toFixed(2)
                                    : ""}
                        </td>
                      ))}
                    </tr>
                  ))
              ) : (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-2 text-center"
                  >
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
            {isTotalHeadingVisible && (
              <tfoot>
                <tr className="border-t border-gray-400 font-bold">
                  <td colSpan={columns.length - 2} className="px-4 py-2">
                    Total
                  </td>
                  <td className="px-4 py-2 text-right">
                    {totals.totalDebit.toFixed(2)}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {totals.totalCredit.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </>
      )}

      {/* Click outside to close export options */}
      {showExportOptions && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setShowExportOptions(false)}
        />
      )}
    </div>
  );
};

export default TrialBalance;